import { useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { useAuthStore } from "../auth/auth-store.js";
import { getEnabledModules } from "../modules/index.js";
import { resolveNotificationTarget } from "./notification-target.js";

/** How a caller opens a module entity — the shell's own entity-tab contract. */
export type OpenModuleEntityTab = (args: {
  moduleId: string;
  entityId: string;
  title: string;
  search?: string;
}) => void;

/**
 * Activate a notification's link (PB-1583).
 *
 * The shared half of the fix: both notification surfaces call this, so "open
 * the shared resource" has one implementation. `openModuleEntityTab` is passed
 * IN rather than reached for, because the two callers get it from different
 * places — the tray renders in the TopBar, outside `ShellBridgeProvider`, and
 * takes it as a prop from `AppLayout`; the full-page feed renders inside the
 * provider and reads it from the bridge.
 *
 * The tab's label is the notification's own title. A server-side deep link
 * carries no entity name, and the shell renames the tab once the module loads
 * the entity — inventing a placeholder id-shaped label would be worse than a
 * temporary one that at least says what the reader clicked.
 */
export function useOpenNotification(
  openModuleEntityTab: OpenModuleEntityTab | undefined,
): (href: string, title: string, eventType?: string) => void {
  const navigate = useNavigate();
  // TENANT-scoped, the same set `AppLayout` mounts routes from. Deployment
  // config alone would call a module enabled that this tenant does not have,
  // and the two halves then disagree: `resolveNotificationTarget` answers
  // `module-entity`, the tenant-scoped `openModuleEntityTab` declines to open
  // a tab for it, and the literal-navigation fallback below is skipped — so
  // the click did nothing at all.
  const tenantEnabledModules = useAuthStore(
    (state) => state.claims?.enabledModules,
  );
  return useCallback(
    (href: string, title: string, eventType?: string): void => {
      const target = resolveNotificationTarget(
        href,
        getEnabledModules(tenantEnabledModules).map((module) => module.id),
        eventType,
      );
      if (target.kind === "module-entity" && openModuleEntityTab) {
        openModuleEntityTab({
          moduleId: target.moduleId,
          entityId: target.entityId,
          title,
          ...(target.search ? { search: target.search } : {}),
        });
        return;
      }
      // Either a literal route, or a module reference with no bridge to
      // resolve it (catalog/tests). Navigating is always safe: the resource
      // may be gone or unreachable, and the module's own not-found state — or
      // the shell's `path="*"` — is what answers, rather than this deciding.
      navigate(target.kind === "navigate" ? target.href : href);
    },
    [navigate, openModuleEntityTab, tenantEnabledModules],
  );
}
